import { useCallback, useMemo, useState } from 'react'

import { useDeleteLicense } from 'commons/hooks/bank/license'
import { FilterCompanyLicensesWebPage } from 'commons/service/bank/companyDetails'
import { Toast } from 'ui/components'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

import { MappedLicense } from '../MappedLicense'

export const useComponentEssence = (
  companyId: number,
  filterCompanyLicensesWebPage: (filter: FilterCompanyLicensesWebPage) => void,
  licensesTotalCount: number
) => {
  const [loading, setLoading] = useState(false)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: companyId,
      page: 1,
      size: 10
    }),
    []
  )

  const [tableFilter, setTableFilter] = useState<{
    relationshipId: number
    page: number
    size: number
  }>(defaultFilter)

  const tableState = useTableState({ filter: {} })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      setLoading(true)

      tableState.onPageChange(page, size)

      const filter = { ...tableFilter, page, size }

      setTableFilter(filter)

      await filterCompanyLicensesWebPage(filter)

      setLoading(false)
    },
    [filterCompanyLicensesWebPage, tableFilter, tableState]
  )

  const getCompanyLicensesAfterDeleteWebPage = () => {
    setLoading(true)

    const isDeletingLicensesLastOnPage = tableFilter.size * tableFilter.page >= licensesTotalCount

    const page = isDeletingLicensesLastOnPage
      ? Math.ceil((licensesTotalCount - 1) / tableFilter.size) || 1
      : tableFilter.page

    filterCompanyLicensesWebPage({
      ...tableFilter,
      page
    })

    tableState.onPageChange(page, tableState.size)

    const filter = { ...tableFilter, page }

    setTableFilter(filter)

    setLoading(false)
  }

  const [licenseToDelete, setLicenseToDelete] = useState<MappedLicense | undefined>(undefined)
  const { routine: deleteLicense } = useDeleteLicense({
    onSuccess: async () => {
      getCompanyLicensesAfterDeleteWebPage()
      Toast.successSmall(`License "${licenseToDelete?.name}" was deleted.`)
    },
    onError: (error) => handlerServerError(error)
  })

  const { Confirmation: DeleteConfirmation, open } = useConfirmation({
    message: `Are you sure you want to delete "${licenseToDelete?.name || 'this'}" license?`,
    onConfirm: () => (licenseToDelete?.id ? deleteLicense(licenseToDelete?.id) : Promise.resolve()),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const openDeleteConfirmation = useCallback(
    (item: MappedLicense) => {
      setLicenseToDelete(item)
      open()
    },
    [open, setLicenseToDelete]
  )

  return { loading, tableState, changePagination, openDeleteConfirmation, DeleteConfirmation }
}
